import { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo, memo } from 'react';
import { ChatMessageResponse } from '@/lib/api';
import { cn } from '@/lib/utils';
import { sanitizeChatHtml } from '@/lib/sanitize-chat-html';
import { toast } from '@/components/ui/use-toast';
import { AttachmentItem } from './AttachmentItem';
import { UserProfileModal } from './UserProfileModal';
import { chatHtmlToPlainText, chatHtmlToCopyText } from '../utils';
import { UserAvatar } from './UserAvatar';
import { useLongPress } from '../hooks/useLongPress';

const QUICK_REACTIONS = ['❤️', '👍', '👎', '🔥', '😂', '👏'];

interface MessageItemProps {
  message: ChatMessageResponse;
  isOwnMessage: boolean;
  isAdmin?: boolean;
  isConsecutive?: boolean;
  isLastInGroup?: boolean;
  onStartEdit?: (messageId: string) => void;
  onDelete?: (messageId: string) => void;
  onReply?: (messageId: string) => void;
  onReaction?: (messageId: string, emoji: string) => void;
  onOpenDm?: (userId: string) => void;
  /** Scroll the list to the message identified by `messageId` (used by reply previews). */
  onJumpToMessage?: (messageId: string) => void;
}

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === 'admin'
      ? 'bg-green-100 text-green-700'
      : role === 'moderator'
        ? 'bg-blue-100 text-blue-700'
        : role === 'ambassador'
          ? 'bg-purple-100 text-purple-700'
          : 'bg-gray-100 text-gray-600';
  return <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>{role}</span>;
}

function ReplyPreview({
  preview,
  isOwnMessage,
  onClick,
}: {
  preview: { senderName: string; content: string };
  isOwnMessage: boolean;
  /** When provided, the preview becomes a button that jumps to the original message. */
  onClick?: () => void;
}) {
  // The stored content is rich-text HTML; render it as plain text so tags don't
  // leak into the preview, and let it wrap/clamp so a long quote can't push the
  // bubble past the viewport edge.
  const previewText = chatHtmlToPlainText(preview.content);
  const className = cn(
    'block w-full text-left mb-1.5 px-2.5 py-1.5 rounded-lg border-l-[3px] overflow-hidden',
    isOwnMessage ? 'bg-black/10 border-green-600' : 'bg-black/[0.07] border-indigo-400',
    onClick &&
      cn(
        'cursor-pointer transition-colors',
        isOwnMessage ? 'hover:bg-black/20' : 'hover:bg-black/[0.12]',
      ),
  );
  const inner = (
    <>
      <p className="text-[12px] font-semibold leading-tight truncate text-black/80">
        {preview.senderName}
      </p>
      <p className="text-[12px] text-black/60 break-words line-clamp-2">{previewText}</p>
    </>
  );

  if (!onClick) {
    return <div className={className}>{inner}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={className}
      aria-label={`Jump to replied message from ${preview.senderName}`}
    >
      {inner}
    </button>
  );
}

function ReactionPills({
  reactions,
  isOwnMessage,
}: {
  reactions: Array<{ emoji: string; count: number; userIds: string[] }>;
  isOwnMessage: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {reactions.map((r) => (
        <span
          key={r.emoji}
          className={cn(
            'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[12px] font-medium text-black/70 cursor-pointer select-none transition-colors',
            isOwnMessage ? 'bg-black/15 hover:bg-black/20' : 'bg-black/10 hover:bg-black/[0.15]',
          )}
        >
          {r.count} {r.emoji}
        </span>
      ))}
    </div>
  );
}

function MenuItem({
  label,
  onClick,
  danger,
}: {
  label: string;
  onClick: () => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full text-left px-[14px] py-2 text-[14px] transition-colors',
        danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50',
      )}
    >
      {label}
    </button>
  );
}

function ContextMenuPanel({
  x,
  y,
  isOwnMessage,
  canDelete,
  onReply,
  onStartEdit,
  onCopy,
  onReaction,
  onDelete,
  onClose,
}: {
  x: number;
  y: number;
  isOwnMessage: boolean;
  canDelete: boolean;
  onReply: () => void;
  onStartEdit: () => void;
  onCopy: () => void;
  onReaction: (emoji: string) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      style={{ left: x, top: y, WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}
      // select-none: the long-press that opens this menu is also the gesture the
      // browser uses to start a text selection — without this, holding to open the
      // menu highlights the emoji row / Reply label and the buttons stop responding
      // (the tap is read as adjusting the selection instead of clicking).
      className="fixed z-50 min-w-[180px] rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.2)] bg-white py-1.5 select-none"
    >
      {/* Quick reactions row */}
      <div className="flex items-center justify-around px-3 py-2 border-b border-gray-100">
        {QUICK_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            onClick={() => {
              onReaction(emoji);
              onClose();
            }}
            className="text-xl hover:scale-125 transition-transform"
          >
            {emoji}
          </button>
        ))}
      </div>
      {/* Action items */}
      <div className="pt-1">
        <MenuItem
          label="Reply"
          onClick={() => {
            onReply();
            onClose();
          }}
        />
        {isOwnMessage && (
          <MenuItem
            label="Edit"
            onClick={() => {
              onStartEdit();
              onClose();
            }}
          />
        )}
        <MenuItem
          label="Copy Text"
          onClick={() => {
            onCopy();
            onClose();
          }}
        />
        {canDelete && (
          <MenuItem
            label="Delete"
            onClick={() => {
              onDelete();
              onClose();
            }}
            danger
          />
        )}
      </div>
    </div>
  );
}

/**
 * Memoized so a re-render of the message list (a new message, a reaction, a
 * typing tick) doesn't re-render every row. Items whose props are unchanged
 * keep their DOM intact — which is what lets an in-progress text selection
 * survive long enough to copy, instead of being wiped by an unrelated update.
 * `addMessage`/`updateMessage` preserve the object reference of untouched
 * messages, so a shallow prop comparison skips them. The parent passes
 * stable (useCallback) handlers.
 */
export const MessageItem = memo(MessageItemImpl);

function MessageItemImpl({
  message,
  isOwnMessage,
  isAdmin,
  isConsecutive,
  isLastInGroup = true,
  onStartEdit,
  onDelete,
  onReply,
  onReaction,
  onOpenDm,
  onJumpToMessage,
}: MessageItemProps) {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [mentionedUserId, setMentionedUserId] = useState<string | null>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const [timestampInline, setTimestampInline] = useState(false);

  // Click delegation — detect clicks on mention spans inside dangerouslySetInnerHTML content
  const handleContentClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = (e.target as HTMLElement).closest('[data-mention]');
    if (!el) return;
    const uid = el.getAttribute('data-user-id');
    if (uid) setMentionedUserId(uid);
  }, []);

  const openMenuAt = useCallback((clientX: number, clientY: number) => {
    const x = Math.min(clientX, window.innerWidth - 200);
    const y = Math.min(clientY, window.innerHeight - 240);
    // Clear any selection the long-press started on the message bubble so it
    // doesn't bleed into the menu that's about to render under the finger.
    window.getSelection()?.removeAllRanges();
    setContextMenu({ x, y });
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      openMenuAt(e.clientX, e.clientY);
    },
    [openMenuAt],
  );

  // Touch devices have no right-click — a long press opens the same menu.
  const longPressHandlers = useLongPress((x, y) => openMenuAt(x, y));

  const handleCopy = useCallback(() => {
    // Copy the rendered text, not the raw HTML — otherwise the clipboard gets
    // literal "<p>" tags and stray leading/trailing newlines ("paragraph
    // marks"). chatHtmlToCopyText strips tags, keeps internal line breaks, and
    // trims the ends.
    navigator.clipboard.writeText(chatHtmlToCopyText(message.content));
    toast({ description: 'Copied to clipboard.' });
  }, [message.content]);

  const hasText = !!(message.content && message.content.trim().length > 0);
  const hasNonText = !!(message.replyPreview || (message.attachmentRefs?.length ?? 0) > 0);

  useLayoutEffect(() => {
    if (hasNonText || !hasText || !textRef.current) {
      setTimestampInline(false);
      return;
    }
    const el = textRef.current;
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight);
    setTimestampInline(el.scrollHeight <= Math.ceil(lineHeight) + 2);
  }, [message.content, hasNonText, hasText]);

  if (message.isDeleted) {
    return <div className="py-1 px-4 text-sm text-muted-foreground/50 italic">Message deleted</div>;
  }

  if (message.messageType === 'system') {
    return (
      <div className="flex items-center justify-center py-2 px-4">
        <p className="text-xs text-muted-foreground italic">{message.content}</p>
      </div>
    );
  }

  const senderInitial = (message.senderName || 'U').charAt(0).toUpperCase();
  const displayName = message.senderName || 'User';
  const timestamp = new Date(message.createdAt).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  const timestampLabel = message.isEdited ? `${timestamp} · edited` : timestamp;
  const canDelete = isOwnMessage || !!isAdmin;
  const outerMargin = isConsecutive ? 'mt-1' : 'mt-3';

  const avatarEl = (
    <UserAvatar userId={message.senderId} displayName={displayName} initial={senderInitial} />
  );

  const replyPreviewEl = message.replyPreview ? (
    <ReplyPreview
      preview={message.replyPreview}
      isOwnMessage={isOwnMessage}
      onClick={
        message.replyToId && onJumpToMessage ? () => onJumpToMessage(message.replyToId!) : undefined
      }
    />
  ) : null;

  const sanitizedContent = useMemo(
    () => (hasText ? sanitizeChatHtml(message.content) : ''),
    [hasText, message.content],
  );

  const textEl = hasText ? (
    <div
      ref={textRef}
      className="chat-message-content text-[14px] leading-[1.4] text-[#111] break-words"
      dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      onClick={handleContentClick}
    />
  ) : null;

  const attachmentsEl =
    message.attachmentRefs && message.attachmentRefs.length > 0 ? (
      <div className="flex flex-col gap-1">
        {message.attachmentRefs.map((ref) => (
          <AttachmentItem
            key={ref.fileId}
            attachment={ref}
            isOwnMessage={isOwnMessage}
            channelId={message.channelId}
          />
        ))}
      </div>
    ) : null;

  const timestampEl = !timestampInline ? (
    <span className="absolute bottom-1.5 right-2.5 text-[11px] text-black/40 pointer-events-none select-none whitespace-nowrap">
      {timestampLabel}
    </span>
  ) : null;

  const inlineTimestampEl = timestampInline ? (
    <span className="text-[11px] text-black/40 pointer-events-none select-none whitespace-nowrap flex-shrink-0 self-end mb-px">
      {timestampLabel}
    </span>
  ) : null;

  const reactionsEl =
    message.reactions && message.reactions.length > 0 ? (
      <ReactionPills reactions={message.reactions} isOwnMessage={isOwnMessage} />
    ) : null;

  const contextMenuEl = contextMenu && (
    <ContextMenuPanel
      x={contextMenu.x}
      y={contextMenu.y}
      isOwnMessage={isOwnMessage}
      canDelete={canDelete}
      onReply={() => onReply?.(message.messageId)}
      onStartEdit={() => onStartEdit?.(message.messageId)}
      onCopy={handleCopy}
      onReaction={(emoji) => onReaction?.(message.messageId, emoji)}
      onDelete={() => onDelete?.(message.messageId)}
      onClose={() => setContextMenu(null)}
    />
  );

  const profileModalEl = mentionedUserId && (
    <UserProfileModal
      userId={mentionedUserId}
      onClose={() => setMentionedUserId(null)}
      onOpenDm={
        onOpenDm
          ? (uid) => {
              onOpenDm(uid);
            }
          : undefined
      }
    />
  );

  if (isOwnMessage) {
    return (
      <>
        <div
          // pointer-coarse:select-none — on touch devices a long-press must not
          // start a native text selection (that's what hijacks the context menu);
          // desktop keeps normal mouse selection for copying.
          className={`flex justify-end items-end gap-2 px-4 pointer-coarse:select-none ${outerMargin}`}
          style={{ WebkitTouchCallout: 'none' }}
          onContextMenu={handleContextMenu}
          {...longPressHandlers}
        >
          <div className="flex flex-col items-end max-w-[70%]">
            <div
              className={cn(
                'relative bg-[#cfe8b4] px-[14px] pt-[10px] min-w-[80px]',
                timestampInline ? 'pb-[10px]' : 'pb-5',
              )}
              style={{ borderRadius: isLastInGroup ? '16px 6px 16px 16px' : '16px 6px 6px 16px' }}
            >
              {replyPreviewEl}
              {timestampInline ? (
                <div className="flex items-end gap-1.5">
                  <div className="flex-1 min-w-0">{textEl}</div>
                  {inlineTimestampEl}
                </div>
              ) : (
                textEl
              )}
              {attachmentsEl}
              {reactionsEl}
              {timestampEl}
            </div>
          </div>
          <div className="w-9 flex-shrink-0">{!isConsecutive && avatarEl}</div>
          {contextMenuEl}
        </div>
        {profileModalEl}
      </>
    );
  }

  return (
    <>
      <div
        // pointer-coarse:select-none — see the own-message branch above.
        className={`flex items-end gap-2 px-4 pointer-coarse:select-none ${outerMargin}`}
        style={{ WebkitTouchCallout: 'none' }}
        onContextMenu={handleContextMenu}
        {...longPressHandlers}
      >
        <div className="w-9 flex-shrink-0">{!isConsecutive && avatarEl}</div>
        <div className="flex flex-col max-w-[70%]">
          {!isConsecutive && message.senderRole && message.senderRole !== 'user' && (
            <div className="flex items-center gap-1.5 mb-1">
              <RoleBadge role={message.senderRole} />
            </div>
          )}
          <div
            className={cn(
              'relative bg-[#f1f1f1] px-[14px] pt-[10px] min-w-[80px]',
              timestampInline ? 'pb-[10px]' : 'pb-5',
            )}
            style={{ borderRadius: isLastInGroup ? '6px 16px 16px 16px' : '6px 16px 16px 6px' }}
          >
            <span className="block text-[11px] text-black/60 mb-1 leading-none">{displayName}</span>
            {replyPreviewEl}
            {timestampInline ? (
              <div className="flex items-end gap-1.5">
                <div className="flex-1 min-w-0">{textEl}</div>
                {inlineTimestampEl}
              </div>
            ) : (
              textEl
            )}
            {attachmentsEl}
            {reactionsEl}
            {timestampEl}
          </div>
        </div>
        {contextMenuEl}
      </div>
      {profileModalEl}
    </>
  );
}
